/**
 * UI Message Stream Parser
 * 
 * Parses the Vercel AI SDK UI Message Stream format for ChatGPT-style UX.
 * 
 * Stream format:
 * - text-start: {"type":"text-start","id":"..."}
 * - text-delta: {"type":"text-delta","id":"...","delta":"..."}
 * - text-end: {"type":"text-end","id":"..."}
 * - data: {"type":"data","data":{...}}  (custom data parts including tool status)
 * - error: {"type":"error","errorText":"..."}
 * 
 * Also supports legacy format for backward compatibility:
 * - {"type":"content","text":"..."}
 * - {"type":"tool","tool":"..."}
 * - {"type":"done"}
 */

/**
 * Parse a single SSE data line and return structured event
 */
export function parseStreamEvent(jsonStr) {
  try {
    const data = JSON.parse(jsonStr);
    return data;
  } catch (e) {
    console.warn("Failed to parse stream event:", e);
    return null;
  }
}

/**
 * Categorize a stream event for UI rendering
 * Returns: { category: 'text' | 'status' | 'tool' | 'debug' | 'error' | 'ignore', ... }
 */
export function categorizeStreamEvent(event) {
  if (!event || typeof event !== 'object') {
    return { category: 'ignore' };
  }

  // New UI Message Stream format
  switch (event.type) {
    // Explicitly ignore tool-related events - these should NOT be rendered as text
    case 'tool-input-start':
    case 'tool-input-delta':
    case 'tool-input-end':
    case 'tool-input-available':
    case 'tool-output-available':
    case 'tool-call':
    case 'tool-result':
    case 'tool-error':
    case 'start':
    case 'start-step':
    case 'finish-step':
    case 'finish':
      return { category: 'ignore' };

    case 'text-start':
      return { category: 'ignore', id: event.id };

    case 'text-delta':
      return {
        category: 'text',
        id: event.id,
        delta: event.delta
      };

    case 'text-end':
      return { category: 'ignore', id: event.id };

    case 'data-tool-status':
      // Tool status updates from UI Message Stream
      return {
        category: 'status',
        tool: event.data?.tool,
        status: event.data?.status,
      };
    
    case 'data':
      // Other data parts are for debug view
      return { category: 'debug', data: event.data };
    
    case 'error':
      return { 
        category: 'error', 
        errorText: event.errorText || 'An error occurred' 
      };

    // Legacy format support
    case 'content':
      return { 
        category: 'text', 
        delta: event.text,
        legacy: true 
      };
    
    case 'tool':
      return { 
        category: 'status', 
        tool: event.tool,
        status: getToolStatusMessage(event.tool),
        legacy: true 
      };
    
    case 'done':
      return { category: 'ignore', done: true };

    default:
      // Check for legacy partial content format
      if (event.content?.parts?.[0]?.text && event.partial === true) {
        return { 
          category: 'text', 
          delta: event.content.parts[0].text,
          legacy: true,
          fullReplace: true 
        };
      }
      // Check for function calls (tool invocations)
      if (event.content?.parts?.[0]?.functionCall) {
        const toolName = event.content.parts[0].functionCall.name;
        return {
          category: 'status',
          tool: toolName,
          status: getToolStatusMessage(toolName),
          legacy: true
        };
      }
      return { category: 'debug', data: event };
  }
}

/**
 * Tool status messages (duplicated from backend for client-side use)
 */
const toolStatusMessages = {
  // Core utilities
  get_current_time: "Checking current time…",
  search_web: "Searching the web…",
  scrape_url: "Reading web content…",
  deep_research: "Researching deeply…",
  
  // Productivity
  manage_productivity: "Managing tasks & notes…",
  manage_reminders: "Processing reminders…",
  manage_sessions: "Handling sessions…",
  manage_scheduled_tasks: "Managing scheduled tasks…",
  
  // Memory
  manage_memory: "Accessing memory…",
  
  // Data management
  manage_groceries: "Managing groceries…",
  manage_meetings: "Processing meetings…",
  manage_contacts: "Managing contacts…",
  
  // Storage
  manage_files: "Handling files…",
  
  // Notifications
  manage_notifications: "Managing notifications…",
  
  // Google Integrations
  manage_integration: "Working with integration…",
  manage_calendar: "Accessing calendar…",
  manage_gmail: "Processing email…",
  manage_drive: "Accessing Drive…",
  manage_docs: "Working with Docs…",
  manage_sheets: "Working with Sheets…",
  
  // Legacy tool names (for backward compatibility)
  search_agent: "Retrieving web results…",
  tasks_lists_notes_agent: "Managing tasks & notes…",
  reminders_agent: "Processing reminders…",
  contact_agent: "Managing contacts…",
  session_agent: "Handling sessions…",
  notion_assistant: "Syncing with Notion…",
  retrival_assistant: "Fetching meeting data…",
  scraping_agent: "Reading web content…",
  calendar_agent: "Processing calendar…",
  gmail_agent: "Processing Gmail…",
  get_history: "Loading chat history…",
  read_image_file: "Analyzing image…",
  search_in_history: "Searching conversations…",
  execute_db_query: "Querying database…",
  add_to_second_brain_project: "Adding to Second Brain…",
  get_to_second_brain: "Retrieving from Second Brain…",
  search_in_second_brain: "Searching Second Brain…",
};

/**
 * Get user-friendly status message for a tool
 */
export function getToolStatusMessage(toolName) {
  return toolStatusMessages[toolName] || "Working…";
}

/**
 * Check if DEBUG_UI mode is enabled
 */
export function isDebugUIEnabled() {
  if (typeof window !== 'undefined') {
    // Check URL param or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('DEBUG_UI') === 'true') return true;
    if (localStorage.getItem('DEBUG_UI') === 'true') return true;
  }
  return false;
}

/**
 * Stream event accumulator for building message content
 */
export class StreamEventAccumulator {
  constructor() {
    this.textContent = "";
    this.currentStatus = null;
    this.debugParts = [];
    this.toolsUsed = [];
    this.hasError = false;
    this.errorText = null;
  }

  processEvent(event) {
    const categorized = categorizeStreamEvent(event);
    
    switch (categorized.category) {
      case 'text':
        if (categorized.fullReplace) {
          // Legacy format that sends full text each time
          this.textContent = categorized.delta;
        } else {
          this.textContent += categorized.delta;
        }
        // Clear status when text is coming in
        this.currentStatus = null;
        break;
      
      case 'status':
        this.currentStatus = categorized.status;
        if (categorized.tool && !this.toolsUsed.includes(categorized.tool)) {
          this.toolsUsed.push(categorized.tool);
        }
        break;
      
      case 'error':
        this.hasError = true;
        this.errorText = categorized.errorText;
        break;
      
      case 'debug':
        this.debugParts.push(categorized.data);
        break;
    }

    return categorized;
  }

  getTextContent() {
    return this.textContent;
  }

  getCurrentStatus() {
    return this.currentStatus;
  }

  getDebugInfo() {
    return {
      toolsUsed: this.toolsUsed,
      debugParts: this.debugParts,
    };
  }

  reset() {
    this.textContent = "";
    this.currentStatus = null;
    this.debugParts = [];
    this.toolsUsed = [];
    this.hasError = false;
    this.errorText = null;
  }
}
